import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ExecutionSource,
  GetQueryTemplatesQuery,
  GetQueryTemplatesQueryVariables,
  QueryTemplate,
  QueryType,
} from '../../../../API';
import { GetQueryTemplates } from '../../../../graphql/queries';
import {
  FilterGroup,
  QueryRecord,
  StandardTemplate,
  StandardTemplateFilter,
} from '../../models/market-fan-exploration.model';
import { FanExplorationService } from '../../services/fan-exploration.service';
import { QueryWithExecutionRecord } from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';

@Component({
  selector: 'app-standard-templates',
  templateUrl: './standard-templates.component.html',
  styleUrl: './standard-templates.component.scss',
})
export class StandardTemplatesComponent implements OnInit, OnChanges {
  private static readonly ALL_FILTER_OPTION_VALUE = '__ALL__';

  private static readonly EMPTY_FILTER_OPTIONS: { label: string; value: string }[] = [];

  private static readonly EMPTY_FILTER_VALUES: string[] = [];

  @Input() editRecord: QueryWithExecutionRecord | null = null;
  @Output() editQueryRequested = new EventEmitter<QueryRecord>();
  @Output() historyTabRequested = new EventEmitter<void>();

  isCodeView = false;
  isEditQueryModalVisible = false;
  templates: StandardTemplate[] = [];
  templateOptions: { label: string; value: string }[] = [];
  filterGroups: FilterGroup[] = [];
  generatedQueryText = '';
  isSavingDraft = false;
  isSubmittingQuery = false;
  queryId = '';
  executionId = '';

  readonly standardTemplateForm: FormGroup;

  get selectedTemplateDescription(): string {
    const selectedTemplateId = this.standardTemplateForm.get('templateId')?.value;
    const template = this.templates.find((item) => item.templateId === selectedTemplateId);

    return template?.description?.trim() ?? '-';
  }

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private messageService: NzMessageService,
    private formBuilder: FormBuilder,
    private fanExplorationService: FanExplorationService,
  ) {
    this.standardTemplateForm = this.formBuilder.group({
      templateId: this.formBuilder.control<string | null>(null, [Validators.required]),
      alias: this.formBuilder.control<string>('', [
        Validators.required,
        Validators.maxLength(45),
        Validators.pattern(AT_LEAST_ONE_ALPHABET),
        Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
      ]),
      filters: this.formBuilder.group({}),
    });
  }

  ngOnInit(): void {
    this.standardTemplateForm
      .get('templateId')
      ?.valueChanges.subscribe((templateId) => this.onTemplateChange(templateId));
    this.standardTemplateForm.valueChanges.subscribe(() => this.refreshGeneratedQuery());
    void this.loadTemplates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editRecord'] && this.editRecord) {
      this.applyEditRecord(this.editRecord);
    }
  }

  get generatedQuery(): string {
    return this.generatedQueryText;
  }

  get isViewCodeDisabled(): boolean {
    const selectedTemplateId = this.standardTemplateForm.get('templateId')?.value;
    return !selectedTemplateId;
  }

  private refreshGeneratedQuery(): void {
    const selectedTemplateId = this.standardTemplateForm.get('templateId')?.value;
    const template = this.templates.find((temp) => temp.templateId === selectedTemplateId);
    let query = template?.query ?? '';

    this.filterGroups.forEach((group) => {
      const selectedValues = this.getSelectedFilterValues(group.name);
      if (Array.isArray(selectedValues) && selectedValues.length > 0) {
        query = query.replaceAll(`:${group.name}`, selectedValues.map((value) => `'${value}'`).join(','));
      }
    });

    this.generatedQueryText = query;

    if (this.isViewCodeDisabled && this.isCodeView) {
      this.isCodeView = false;
    }
  }

  get filtersFormGroup(): FormGroup {
    return this.standardTemplateForm.get('filters') as FormGroup;
  }

  getFilterControl(name: string): FormControl {
    return this.filtersFormGroup.get(name) as FormControl;
  }

  get aliasFormControl(): FormControl {
    return this.standardTemplateForm.get('alias') as FormControl;
  }

  private async loadTemplates(): Promise<void> {
    const orgId = this.userManagementService.getOrgId();

    if (!orgId) {
      this.messageService.warning('Unable to load templates: orgId is missing for the current user.');
      return;
    }

    try {
      const response = await this.apiService.query<GetQueryTemplatesQuery, GetQueryTemplatesQueryVariables>(
        GetQueryTemplates,
        { orgId },
      );

      this.extractTemplateData(response.data?.GetQueryTemplates || []);
    } catch (error) {
      this.messageService.error('Failed to load templates. Please try again.');
      console.error('Error fetching query templates', error);
    }
  }

  private extractTemplateData(templateList: (QueryTemplate | StandardTemplate)[]): void {
    this.templates = templateList.map((template) => this.normalizeTemplate(template));

    this.templateOptions = this.templates.map((template) => ({
      label: template.name,
      value: template.templateId,
    }));

    if (this.editRecord) {
      this.applyEditRecord(this.editRecord);
      return;
    }

    const selectedTemplateId = this.standardTemplateForm.get('templateId')?.value;
    if (!selectedTemplateId && this.templateOptions.length > 0) {
      this.standardTemplateForm.patchValue({ templateId: this.templateOptions[0].value }, { emitEvent: false });
    }

    this.buildFilterGroups(this.standardTemplateForm.get('templateId')?.value);
  }

  private applyEditRecord(record: QueryWithExecutionRecord): void {
    if (!this.templates.length) {
      return;
    }

    const templateId = record.templateId?.trim() || null;
    const alias = record.queryName?.trim() || '';

    this.queryId = record.queryId?.trim() || '';
    this.executionId = record.executionId?.trim() || '';

    this.buildFilterGroups(templateId);
    this.standardTemplateForm.patchValue({ templateId, alias }, { emitEvent: false });

    const rawConfig = record.selectedFilterConfig?.trim();
    if (!rawConfig) {
      this.refreshGeneratedQuery();
      return;
    }

    try {
      const filterValues = JSON.parse(rawConfig) as Record<string, string[]>;
      const filtersGroup = this.filtersFormGroup;

      Object.entries(filterValues).forEach(([key, values]) => {
        if (filtersGroup.get(key) && Array.isArray(values)) {
          filtersGroup.get(key)!.setValue(values, { emitEvent: false });
        }
      });

      this.syncDependentFilterGroups();
    } catch (error) {
      console.warn('Unable to parse selectedFilterConfig for standard template edit prefill', error);
    }

    this.refreshGeneratedQuery();
  }

  onTemplateChange(templateId: string | null): void {
    this.buildFilterGroups(templateId, true);
  }

  onFilterSelectionChange(groupName: string, selectedValues: string[]): void {
    const group = this.filterGroups.find((filterGroup) => filterGroup.name === groupName);
    if (!group) {
      return;
    }

    const availableValues = this.getAvailableFilterValues(group);
    const normalizedValues = selectedValues.includes(StandardTemplatesComponent.ALL_FILTER_OPTION_VALUE)
      ? availableValues
      : selectedValues.filter((value) => availableValues.includes(value));

    if (this.areStringArraysEqual(selectedValues, normalizedValues)) {
      this.syncDependentFilterGroups();
      return;
    }

    this.getFilterControl(groupName).setValue(normalizedValues, { emitEvent: false });
    this.syncDependentFilterGroups();
    this.refreshGeneratedQuery();
  }

  private buildFilterGroups(templateId: string | null, clearSelections = false): void {
    const selectedTemplate = this.templates.find((template) => template.templateId === templateId);
    const templateFilters = selectedTemplate?.filters || [];

    this.filterGroups = templateFilters.map((filter) => {
      let rawValues: string[] | Record<string, string[]> | undefined;
      if (typeof filter.values === 'string') {
        try {
          rawValues = JSON.parse(filter.values as string) as string[] | Record<string, string[]>;
        } catch (e) {
          console.warn('Unable to parse filter.values JSON string in buildFilterGroups for filter', filter.name, e);
          rawValues = undefined;
        }
      } else {
        rawValues = filter.values as string[] | Record<string, string[]> | undefined;
      }

      const uniqueValues = Array.from(
        new Set(
          Array.isArray(rawValues)
            ? rawValues
            : Object.values(rawValues || {}).flatMap(
                (valueList) => valueList || StandardTemplatesComponent.EMPTY_FILTER_VALUES,
              ),
        ),
      );

      return {
        name: filter.name,
        displayName: filter.displayName?.trim() || filter.name,
        allOptions: uniqueValues.map((value) => ({
          label: value,
          value,
        })),
        options: [],
        dependent: Boolean(filter.dependent),
        parentFilterName: filter.parentFilterName,
        values: rawValues || [],
      };
    });

    const nextFiltersGroup = this.formBuilder.group({});
    const previousFiltersValue = clearSelections
      ? ({} as Record<string, string[]>)
      : (this.filtersFormGroup.value as Record<string, string[]>);
    const nextSelectedValues: Record<string, string[]> = {};

    this.filterGroups.forEach((group) => {
      const previousValue = previousFiltersValue[group.name] || [];
      const resolvedOptions = this.getResolvedOptionsForGroup(group, nextSelectedValues);
      group.options = this.buildDisplayOptions(resolvedOptions);
      const allowedValues = resolvedOptions.map((option) => option.value);
      const validPreviousValues = previousValue.filter((value) => allowedValues.includes(value));
      const control = this.formBuilder.control<string[]>(validPreviousValues, [Validators.required]);

      if (group.dependent && resolvedOptions.length === 0) {
        control.disable({ emitEvent: false });
      }

      nextFiltersGroup.addControl(group.name, control);
      nextSelectedValues[group.name] = validPreviousValues;
    });

    this.standardTemplateForm.setControl('filters', nextFiltersGroup);
    this.syncDependentFilterGroups();
    this.refreshGeneratedQuery();
  }

  private getSelectedFilterValues(groupName: string): string[] {
    const group = this.filterGroups.find((filterGroup) => filterGroup.name === groupName);
    const selectedValues = this.getFilterControl(groupName).value as string[];
    if (!group || !Array.isArray(selectedValues)) {
      return [];
    }

    const availableValues = this.getAvailableFilterValues(group);
    return selectedValues.filter((value) => availableValues.includes(value));
  }

  private getAvailableFilterValues(group: FilterGroup): string[] {
    return group.options
      .filter((option) => option.value !== StandardTemplatesComponent.ALL_FILTER_OPTION_VALUE)
      .map((option) => option.value);
  }

  private syncDependentFilterGroups(): void {
    const selectedValuesByFilter = this.filtersFormGroup.getRawValue() as Record<string, string[]>;

    this.getFilterProcessingOrder().forEach((groupName) => {
      const group = this.filterGroups.find((item) => item.name === groupName);
      if (!group) {
        return;
      }

      const control = this.getFilterControl(group.name);
      const resolvedOptions = this.getResolvedOptionsForGroup(group, selectedValuesByFilter);
      group.options = this.buildDisplayOptions(resolvedOptions);

      const availableValues = resolvedOptions.map((option) => option.value);
      const selectedValues = (control.value as string[]) || [];
      const normalizedSelectedValues = selectedValues.includes(StandardTemplatesComponent.ALL_FILTER_OPTION_VALUE)
        ? availableValues
        : selectedValues;
      const normalizedValue = normalizedSelectedValues.filter((value) => availableValues.includes(value));

      if (!this.areStringArraysEqual((control.value as string[]) || [], normalizedValue)) {
        control.setValue(normalizedValue, { emitEvent: false });
      }

      if (group.dependent && resolvedOptions.length === 0) {
        if (control.enabled) {
          control.disable({ emitEvent: false });
        }
      } else if (control.disabled) {
        control.enable({ emitEvent: false });
      }

      selectedValuesByFilter[group.name] = normalizedValue;
    });
  }

  private getFilterProcessingOrder(): string[] {
    const indegree = new Map<string, number>();
    const childrenByParent = new Map<string, string[]>();

    this.filterGroups.forEach((group) => {
      indegree.set(group.name, 0);
    });

    this.filterGroups.forEach((group) => {
      if (group.parentFilterName && indegree.has(group.parentFilterName)) {
        const children = childrenByParent.get(group.parentFilterName) || [];
        children.push(group.name);
        childrenByParent.set(group.parentFilterName, children);
        indegree.set(group.name, (indegree.get(group.name) || 0) + 1);
      }
    });

    const queue: string[] = [];
    indegree.forEach((value, key) => {
      if (value === 0) {
        queue.push(key);
      }
    });

    const ordered: string[] = [];

    while (queue.length > 0) {
      const current = queue.shift()!;
      ordered.push(current);

      (childrenByParent.get(current) || []).forEach((child) => {
        const nextDegree = (indegree.get(child) || 0) - 1;
        indegree.set(child, nextDegree);
        if (nextDegree === 0) {
          queue.push(child);
        }
      });
    }

    const unresolved = this.filterGroups.map((group) => group.name).filter((name) => !ordered.includes(name));
    return [...ordered, ...unresolved];
  }

  private getResolvedOptionsForGroup(
    group: FilterGroup,
    selectedValuesByFilter: Record<string, string[]>,
  ): {
    label: string;
    value: string;
  }[] {
    if (!group.dependent || !group.parentFilterName) {
      return group.allOptions;
    }

    const parentSelectedValues = selectedValuesByFilter[group.parentFilterName] || [];
    if (parentSelectedValues.length === 0) {
      return StandardTemplatesComponent.EMPTY_FILTER_OPTIONS;
    }

    const dependencyMap = Array.isArray(group.values)
      ? ({} as Record<string, string[]>)
      : ((group.values || {}) as Record<string, string[]>);
    const resolvedValues = Array.from(
      new Set(
        parentSelectedValues.flatMap((value) => dependencyMap[value] || StandardTemplatesComponent.EMPTY_FILTER_VALUES),
      ),
    );
    const resolvedValueSet = new Set(resolvedValues);

    return group.allOptions.filter((option) => resolvedValueSet.has(option.value));
  }

  private buildDisplayOptions(options: { label: string; value: string }[]): { label: string; value: string }[] {
    if (options.length === 0) {
      return StandardTemplatesComponent.EMPTY_FILTER_OPTIONS;
    }

    return [
      {
        label: 'All',
        value: StandardTemplatesComponent.ALL_FILTER_OPTION_VALUE,
      },
      ...options,
    ];
  }

  private normalizeTemplate(template: QueryTemplate | StandardTemplate): StandardTemplate {
    const normalizedFilters = ((template.filters || []) as StandardTemplateFilter[]).map((filter) => {
      let parsedValues: string[] | Record<string, string[]> | undefined;

      if (typeof filter.values === 'string') {
        try {
          parsedValues = JSON.parse(filter.values as string) as string[] | Record<string, string[]>;
        } catch (e) {
          console.warn('Unable to parse filter.values JSON string for filter', filter.name, e);
          parsedValues = undefined;
        }
      } else {
        parsedValues = filter.values as string[] | Record<string, string[]> | undefined;
      }

      const normalizedValues = Array.isArray(parsedValues)
        ? Array.from(new Set(parsedValues || []))
        : Object.entries(parsedValues || {}).reduce<Record<string, string[]>>((acc, [key, valueList]) => {
            acc[key] = Array.from(new Set(valueList || []));
            return acc;
          }, {});

      return {
        ...filter,
        values: normalizedValues,
        dependent: Boolean(filter.dependent),
        parentFilterName: filter.parentFilterName?.trim() || undefined,
      } as StandardTemplateFilter;
    });

    return {
      ...template,
      filters: normalizedFilters,
    };
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  async onGenerateDataClick(): Promise<void> {
    if (this.standardTemplateForm.invalid) {
      this.standardTemplateForm.markAllAsTouched();
      this.messageService.warning('Please complete required fields before generating data.');
      return;
    }

    this.isSubmittingQuery = true;

    try {
      const draftSaved = await this.saveDraftAndSetIds();
      if (!draftSaved) {
        this.messageService.error('Unable to save template before generating data.');
        return;
      }

      const response = await this.fanExplorationService.submitQuery({
        queryId: this.queryId,
        executionId: this.executionId,
      });

      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.messageService.success('Template validated and submitted successfully.');
        this.historyTabRequested.emit();
        return;
      }

      this.messageService.error('Unable to validate and submit template query.');
    } catch (error) {
      console.error('Error validating and submitting template query', error);
      this.messageService.error('Failed to validate and submit template query. Please try again.');
    } finally {
      this.isSubmittingQuery = false;
    }
  }

  private async saveDraftAndSetIds(): Promise<boolean> {
    if (this.standardTemplateForm.invalid) {
      this.standardTemplateForm.markAllAsTouched();
      this.messageService.warning('Please complete required fields before saving templated query.');
      return false;
    }

    const selectedTemplateId = this.standardTemplateForm.get('templateId')?.value;
    const selectedTemplate = this.templates.find((template) => template.templateId === selectedTemplateId);
    const alias = (this.aliasFormControl.value || '').trim();

    this.isSavingDraft = true;

    try {
      const response = await this.fanExplorationService.saveDraft({
        description: selectedTemplate?.description || '',
        executionSource: ExecutionSource.WORKBENCH,
        queryId: this.queryId,
        executionId: this.executionId,
        name: alias || selectedTemplate?.name || 'Standard Query Template',
        query: this.generatedQuery,
        queryType: QueryType.STANDARD,
        selectedFilterConfig: JSON.stringify(this.filtersFormGroup.getRawValue()),
        templateId: selectedTemplateId || undefined,
      });

      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.messageService.success('Templated query saved successfully.');
        return true;
      }

      this.messageService.error('Unable to save templated query.');
      return false;
    } catch (error) {
      console.error('Error saving templated query', error);
      this.messageService.error('Failed to save templated query. Please try again.');
      return false;
    } finally {
      this.isSavingDraft = false;
    }
  }

  async onSaveAsDraftClick(): Promise<void> {
    await this.saveDraftAndSetIds();
  }

  toggleCodeQueryView(): void {
    this.isCodeView = !this.isCodeView;
  }

  onEditCodeClick(): void {
    this.isEditQueryModalVisible = true;
  }

  closeEditQueryModal(): void {
    this.isEditQueryModalVisible = false;
  }

  confirmEditQuery(): void {
    this.isEditQueryModalVisible = false;
    this.isCodeView = false;
    this.editQueryRequested.emit({
      name: (this.aliasFormControl.value || '').trim(),
      query: this.generatedQuery,
    });
  }

  get isMarketingUser(): boolean {
    return this.userManagementService.isMarketingUser();
  }
}
